import {useEffect, useState} from 'react';

export const BREAKPOINTS = {
  mobile: 767,
  tablet: 1023
};

export type DeviceType = 'mobile' | 'tablet' | 'pc';

const debounce = (func: () => void, delay = 150) => {
  let timer: ReturnType<typeof setTimeout>;
  return () => {
    clearTimeout(timer);
    timer = setTimeout(func, delay);
  };
};

const getVh = () => {
  if (typeof window !== 'undefined') {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
    return vh;
  }
  return 0;
};

const isIOS = () => {
  if (typeof navigator === 'undefined') return false;
  return /iPhone|iPad|iPod/.test(navigator.userAgent);
};

const isSafari = () => {
  if (typeof navigator === 'undefined') return false;
  return /^((?!chrome|android).)*safari/i.test(navigator.userAgent) || navigator.userAgent.includes('Safari');
};

export const useResponsive = () => {
  const getWidth = () => (typeof window !== 'undefined' ? window.innerWidth : 1024);
  const [width, setWidth] = useState<number>(getWidth());
  const [vh, setVh] = useState<number>(getVh());

  const isMobile = width <= BREAKPOINTS.mobile;
  const isTablet = width > BREAKPOINTS.mobile && width <= BREAKPOINTS.tablet;
  const isPC = width > BREAKPOINTS.tablet;

  let device: DeviceType = 'mobile';
  if (isTablet) device = 'tablet';
  if (isPC) device = 'pc';

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = debounce(() => {
      setWidth(window.innerWidth);
      const newVh = getVh();
      setVh(newVh);
    });

    handleResize(); // 초기 실행

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  return {
    width,
    vh,
    isMobile,
    isTablet,
    isPC,
    device,
    isIOS: isIOS(),
    isSafari: isSafari()
  };
};
